import { Popconfirm } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { TFunction } from 'i18next';
import { FileTemplate } from 'models/FileTemplate';
import { FileTemplateInput } from 'models/FileTemplateInput';
import React from 'react';

import nameof from 'ts-nameof.macro';

import { v4 as uuidv4 } from 'uuid';
import { RowSelectionType } from 'antd/lib/table/interface';
import { menuReportRepository } from 'views/MenuReportView/MenuReportRepository';
import { renderMasterIndex } from 'helpers/ant-design/table';
import { formService } from 'core/services';
import Form from 'antd/lib/form';
import InputNumber from 'components/InputNumber/InputNumber';
import { generalLanguageKeys } from 'config/consts';
import Input from 'components/Input/Input';
import './../MenuReportDetail.scss';

const { Item: FormItem } = Form;
type KeyType = string | number;
type Loading = 'default' | 'submitting' | 'submitted' | 'error';

enum FileTemplateInputEnum {
  UPDATE_ALL,
  UPDATE_FILE_TEMPLATE_INPUTS,
  UPDATE_VISIBLE,
  UPDATE_CURRENT_FILE_TEMPLATE,
}

interface FileTemplateInputState {
  fileTemplateInputs?: FileTemplateInput[];
  visible?: boolean;
  fileTemplate?: FileTemplate;
}

interface FileTemplateInputAction {
  type: FileTemplateInputEnum;
  data: FileTemplateInputState;
}

function fileTemplateInputReducer(
  state: FileTemplateInputState,
  action: FileTemplateInputAction,
): FileTemplateInputState {
  switch (action.type) {
    case FileTemplateInputEnum.UPDATE_ALL:
      return {
        ...state,
        fileTemplateInputs: [...action.data.fileTemplateInputs],
        visible: action.data.visible,
        fileTemplate: { ...action.data.fileTemplate },
      };
    case FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS:
      return {
        ...state,
        fileTemplateInputs: [...action.data.fileTemplateInputs],
      };
    case FileTemplateInputEnum.UPDATE_VISIBLE:
      return {
        ...state,
        visible: action.data.visible,
      };
  }
}

export default function useFileTemplateInputModalHook(
  updateFileTempalateInputs: (
    fileTemplateInputs: FileTemplateInput[],
    fileTemplate: FileTemplate,
  ) => void,
  translate: TFunction,
  isPreview?: boolean,
  menuReportId?: number,
) {
  const [
    { fileTemplateInputs: fileTemplateInputsInternal, visible, fileTemplate },
    dispatchValue,
  ] = React.useReducer(fileTemplateInputReducer, {
    fileTemplateInputs: [],
    visible: false,
  });

  const handleAddFileTemplateInput = React.useCallback(() => {
    dispatchValue({
      type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
      data: {
        fileTemplateInputs: [
          { ...new FileTemplateInput(), key: uuidv4() },
          ...fileTemplateInputsInternal,
        ],
      },
    });
  }, [fileTemplateInputsInternal]);

  const handleChangeFileTemplateInputCell = React.useCallback(
    (index: number, fieldKey: string) => (value: string | number) => {
      const fileTemplateInputValues = [...fileTemplateInputsInternal];
      const currentFileTemplateInput = fileTemplateInputValues[index];
      currentFileTemplateInput[fieldKey] = value;
      dispatchValue({
        type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
        data: {
          fileTemplateInputs: fileTemplateInputValues,
        },
      });
    },
    [fileTemplateInputsInternal],
  );

  const handleDeleteRow = React.useCallback(
    (index: number) => () => {
      if (index !== null) {
        fileTemplateInputsInternal.splice(index, 1);
        dispatchValue({
          type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
          data: {
            fileTemplateInputs: fileTemplateInputsInternal,
          },
        });
      }
    },
    [fileTemplateInputsInternal],
  );

  const handleOpenModal = React.useCallback((fileTemplate: FileTemplate) => {
    dispatchValue({
      type: FileTemplateInputEnum.UPDATE_ALL,
      data: {
        fileTemplateInputs:
          fileTemplate && fileTemplate.fileTemplateInputs
            ? fileTemplate.fileTemplateInputs.map(item => {
                item.key = uuidv4();
                return item;
              })
            : [],
        visible: true,
        fileTemplate,
      },
    });
  }, []);

  const handleCloseModal = React.useCallback(() => {
    dispatchValue({
      type: FileTemplateInputEnum.UPDATE_VISIBLE,
      data: {
        visible: false,
      },
    });
  }, []);

  const handleSaveModal = React.useCallback(() => {
    menuReportRepository
      .validateInputs({
        menuReportId,
        fileTemplateInputs: [...fileTemplateInputsInternal],
      })
      .then(res => {
        if (res) {
          if (res.isError) {
            dispatchValue({
              type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
              data: {
                fileTemplateInputs: res.fileTemplateInputs,
              },
            });
          } else {
            dispatchValue({
              type: FileTemplateInputEnum.UPDATE_VISIBLE,
              data: {
                visible: false,
              },
            });
            dispatchValue({
              type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
              data: {
                fileTemplateInputs: res.fileTemplateInputs,
              },
            });

            updateFileTempalateInputs(res.fileTemplateInputs, fileTemplate);
          }
        }
      });
  }, [
    fileTemplate,
    fileTemplateInputsInternal,
    menuReportId,
    updateFileTempalateInputs,
  ]);

  const [
    selectedRowKeysFileTemplateInput,
    setSelectedRowKeysFileTemplateInput,
  ] = React.useState<KeyType[]>([]);

  const rowSelectionFileTemplateInput = React.useMemo(
    () => ({
      onChange(selectedRowKeys: KeyType[]) {
        setSelectedRowKeysFileTemplateInput(selectedRowKeys);
      },
      selectedRowKeysFileTemplateInput,
      type: 'checkbox' as RowSelectionType,
      getCheckboxProps: (record: FileTemplate) => ({
        disabled: record.used,
      }),
    }),
    [selectedRowKeysFileTemplateInput],
  );

  const canBulkActionFileTemplateInput = React.useMemo(
    () => selectedRowKeysFileTemplateInput.length > 0,
    [selectedRowKeysFileTemplateInput.length],
  );

  const handleBulkDeleteFileTemplateInput = React.useCallback(() => {
    const selectedRowKeysValue = [...selectedRowKeysFileTemplateInput];
    const listFileTemplateInputValue = [...fileTemplateInputsInternal].filter(
      (currentFile: FileTemplate) =>
        !selectedRowKeysValue.includes(currentFile.key),
    );
    dispatchValue({
      type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
      data: {
        fileTemplateInputs: [...listFileTemplateInputValue],
      },
    });
    setSelectedRowKeysFileTemplateInput([]);
  }, [fileTemplateInputsInternal, selectedRowKeysFileTemplateInput]);

  const columns: ColumnProps<FileTemplateInput>[] = React.useMemo(() => {
    return [
      {
        title: translate('general.columns.index'),
        key: 'index',
        width: 100,
        align: 'center',
        render: renderMasterIndex<FileTemplateInput>(),
      },
      {
        title: translate('fileTemplateInputs.fieldName'),
        key: nameof('fieldName'),
        dataIndex: nameof('fieldName'),
        render(...params: [string, FileTemplateInput, number]) {
          if (isPreview) return params[0];
          else {
            return (
              <FormItem
                validateStatus={formService.getValidationStatus<
                  FileTemplateInput
                >(params[1].errors, nameof(params[1].fieldName))}
                help={params[1].errors?.fieldName}
                className="form-validate"
              >
                <Input
                  type="text"
                  value={params[0]}
                  placeholder={translate(
                    'fileTemplateInputs.placeholder.fieldName',
                  )}
                  className="form-control form-control-sm"
                  onChange={handleChangeFileTemplateInputCell(
                    params[2],
                    'fieldName',
                  )}
                />
              </FormItem>
            );
          }
        },
      },
      {
        title: translate('fileTemplateInputs.fieldValue'),
        key: nameof('fieldValue'),
        dataIndex: nameof('fieldValue'),
        render(...params: [string, FileTemplateInput, number]) {
          if (isPreview) return params[0];
          else
            return (
              <FormItem
                validateStatus={formService.getValidationStatus<
                  FileTemplateInput
                >(params[1]?.errors, nameof(params[1].fieldValue))}
                help={params[1].errors?.fieldValue}
                className="custom-form"
              >
                <Input
                  type="text"
                  value={params[0]}
                  placeholder={translate(
                    'fileTemplateInputs.placeholder.fieldValue',
                  )}
                  className="form-control form-control-sm"
                  onChange={handleChangeFileTemplateInputCell(
                    params[2],
                    'fieldValue',
                  )}
                />
              </FormItem>
            );
        },
      },
      {
        title: translate('fileTemplateInputs.order'),
        width: 200,
        key: nameof('order'),
        dataIndex: nameof('order'),

        render(...params: [number, FileTemplateInput, number]) {
          if (isPreview) return params[0];
          return (
            <FormItem
              validateStatus={formService.getValidationStatus<
                FileTemplateInput
              >(params[1].errors, nameof(params[1].order))}
              help={params[1].errors?.order}
              className="custom-form mt-1"
            >
              <InputNumber
                value={params[0]}
                onChange={handleChangeFileTemplateInputCell(params[2], 'order')}
                allowNegative={false}
                placeholder={translate('fileTemplateInputs.placeholder.order')}
              />
            </FormItem>
          );
        },
      },
      {
        key: nameof('action'),
        dataIndex: nameof('action'),
        width: 60,
        align: 'center',
        render(...params: [string, FileTemplateInput, number]) {
          if (!isPreview)
            return (
              <Popconfirm
                title={translate('general.delete.content')}
                onConfirm={handleDeleteRow(params[2])}
                okText={translate('general.actions.delete')}
                cancelText={translate('general.actions.close')}
              >
                <i className="tio-delete_outlined" color="red" />
              </Popconfirm>
            );
        },
      },
    ];
  }, [
    translate,
    handleChangeFileTemplateInputCell,
    isPreview,
    handleDeleteRow,
  ]);

  const tableFooter = React.useCallback(
    () => (
      <>
        <button className="btn btn-link" onClick={handleAddFileTemplateInput}>
          <i className="fa fa-plus mr-2" />
          {translate(generalLanguageKeys.actions.create)}
        </button>
      </>
    ),
    [handleAddFileTemplateInput, translate],
  );
  return {
    selectedRowKeysFileTemplateInput,
    rowSelectionFileTemplateInput,
    canBulkActionFileTemplateInput,
    setSelectedRowKeysFileTemplateInput,
    handleBulkDeleteFileTemplateInput,
    handleOpenModal,
    handleSaveModal,
    handleCloseModal,
    handleAddFileTemplateInput,
    handleChangeFileTemplateInputCell,
    fileTemplateInputColumns: columns,
    fileTemplateInputsInternal,
    visible,
    tableFooter,
  };
}
